import {
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenu as UI_DropdownMenu,
} from "components/ui/dropdown-menu";
import { Button } from "components/ui/button";
import { toast } from "components/ui/use-toast";
import { useDialogModal } from "../../../../../hook/handle-modal/hooks/actions";
import {
  Trash,
  MoreHorizontal,
  Edit,
  History,
  BarChart3,
  Link2,
} from "lucide-react";
import { IGamification, GamificationStatus } from "../../../../../@types/gamification";
import {
  useInactivateGamificationMutation,
  useLazyGetGamificationHistoryQuery,
  useLazyGetGamificationSummaryQuery,
} from "../../../../../services/gamification";
import { useNavigate } from "react-router-dom";
import { campaignPublicUrl } from "../../constants/campaign-slug";
import { CAMPAIGN_LINK_COPY, CAMPAIGN_MESSAGES } from "../../constants/messages";
import { InactivateConfirmModal } from "./InactivateConfirmModal";
import { HistoryContent } from "./history-content";
import { ReportContent } from "./report-content";

function DropdownMenuComponent({
  trigger,
  row,
}: {
  trigger: React.ReactNode;
  row: IGamification;
}) {
  const { handleModal } = useDialogModal();
  const navigate = useNavigate();
  const [inactivateGamification, { isLoading }] = useInactivateGamificationMutation();
  const [getHistory, { isFetching: isLoadingHistory }] =
    useLazyGetGamificationHistoryQuery();
  const [getSummary, { isFetching: isLoadingReport }] =
    useLazyGetGamificationSummaryQuery();

  const handleViewHistory = async () => {
    if (!row.idCapa) {
      toast(CAMPAIGN_MESSAGES.HISTORY.NO_ID);
      return;
    }

    try {
      const result = await getHistory(row.idCapa).unwrap();
      handleModal({
        isOpen: true,
        title: "Histórico da Campanha",
        description: `Todas as ações registradas para "${row.nickname}".`,
        element: <HistoryContent events={result.events} />,
      });
    } catch (error) {
      toast(CAMPAIGN_MESSAGES.HISTORY.ERROR);
    }
  };

  const handleViewReport = async () => {
    try {
      const summary = await getSummary(row.id).unwrap();
      handleModal({
        isOpen: true,
        title: "Relatório da Campanha",
        description: `Métricas de "${row.nickname}".`,
        element: <ReportContent summary={summary} campaignName={row.nickname} />,
      });
    } catch (error) {
      toast(CAMPAIGN_MESSAGES.REPORT.ERROR);
    }
  };

  const handleInactivate = async () => {
    try {
      await inactivateGamification(row.id).unwrap();
      handleModal({ isOpen: false });
      toast(CAMPAIGN_MESSAGES.INACTIVATE.SUCCESS);
    } catch (error) {
      toast(CAMPAIGN_MESSAGES.INACTIVATE.ERROR);
    }
  };

  const publicUrl = campaignPublicUrl(row.slug);

  const handleCopyLink = async () => {
    if (!publicUrl) return;
    try {
      await navigator.clipboard.writeText(publicUrl);
      toast({ description: CAMPAIGN_LINK_COPY.copied });
    } catch {
      toast({
        variant: "destructive",
        description: CAMPAIGN_LINK_COPY.copyFailed,
      });
    }
  };

  const handleEdit = () => {
    navigate(`/crm/gestao-gameficacao/editar/${row.id}`);
  };

  const openInactivateModal = () => {
    handleModal({
      isOpen: true,
      element: (
        <InactivateConfirmModal
          campaignName={row.nickname}
          onConfirm={handleInactivate}
          onCancel={() => handleModal({ isOpen: false })}
          isLoading={isLoading}
        />
      ),

      title: "Inativar Campanha",
    });
  };

  const isInactive = row.status === GamificationStatus.INATIVA;

  return (
    <>
      <UI_DropdownMenu>
        <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            className="cursor-pointer"
            onClick={handleViewReport}
            disabled={isLoadingReport}
          >
            <BarChart3 className="mr-2 h-4 w-4" />
            {isLoadingReport ? "Carregando..." : "Relatório"}
          </DropdownMenuItem>
          <DropdownMenuItem
            className="cursor-pointer"
            onClick={handleViewHistory}
            disabled={isLoadingHistory}
          >
            <History className="mr-2 h-4 w-4" />
            {isLoadingHistory ? "Carregando..." : "Histórico"}
          </DropdownMenuItem>
          {publicUrl && (
            <DropdownMenuItem
              className="cursor-pointer"
              onClick={handleCopyLink}
            >
              <Link2 className="mr-2 h-4 w-4" />
              {CAMPAIGN_LINK_COPY.copyAction}
            </DropdownMenuItem>
          )}
          {!isInactive && (
            <DropdownMenuItem
              className="cursor-pointer"
              onClick={handleEdit}
            >
              <Edit className="mr-2 h-4 w-4" />
              Editar
            </DropdownMenuItem>
          )}
          {!isInactive && (
            <DropdownMenuItem
              className="cursor-pointer"
              onClick={openInactivateModal}
            >
              <Trash className="mr-2 h-4 w-4" />
              Inativar
            </DropdownMenuItem>
          )}
          {isInactive && (
            <DropdownMenuItem disabled>
              <span className="text-gray-400">Campanha inativa</span>
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </UI_DropdownMenu>
    </>
  );
}

export default function ActionTable({ row }: { row: IGamification }) {
  return (
    <DropdownMenuComponent
      trigger={
        <Button variant="ghost" className="h-8 w-8 p-0 bg-transparent hover:bg-gray-100">
          <span className="sr-only">Abrir menu</span>
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      }
      row={row}
    />
  );
}
